import { get, post } from './client'

function qs(shop: string) {
  return `?shop=${encodeURIComponent(shop)}`
}

export type OrderStatsResponse = {
  total:      number
  fulfilled:  number
  inProgress: number
  returns:    number
  cancelled:  number
}

export type OutboundScanResponse = {
  ok:    boolean
  order: {
    trackingNumber:  string
    orderName:       string
    orderId:         string
    customer:        string
    codAmount:       number
    currentStatus:   string
  }
}

export type OutboundBulkResponse = {
  success: number
  failed:  { orderName: string; reason: string }[]
}

export type OutboundBulkPreviewResponse = {
  valid:     { orderName: string }[]
  conflicts: { orderName: string; currentStatus: string }[]
  notFound:  { orderName: string; reason: string }[]
}

export type ConfirmPickupResponse = {
  updated: number
  failed:  { trackingNumber: string; reason: string }[]
}

export type PickupStatusResponse = {
  awaitingConfirmation: number
  warnings:             number
  orders: {
    trackingNumber: string
    orderName:      string
    pickedUpAt:     string
    hoursElapsed:   number
    status:         'picked_up'
  }[]
  warningOrders: {
    trackingNumber: string
    orderName:      string
    pickedUpAt:     string
    hoursElapsed:   number
  }[]
}

export type OverviewStatsResponse = {
  pipeline: {
    pickedUp:       number
    inTransit:      number
    outForDelivery: number
    delivered:      number
    returned:       number
  }
  timing: {
    avgScToScanOutHours: number | null  // waybill created → scan out (ops dispatch speed)
    peakScanHour:        number | null  // 0-23, hour with most scan_out events
  }
  activity: { period: string; scannedOut: number }
  stale:    { count: number; thresholdDays: number }
}

export const operationsApi = {
  scanOutbound: (shop: string, body: { trackingNumber: string }): Promise<OutboundScanResponse> =>
    post(`/operations/outbound/scan${qs(shop)}`, body),

  confirmPickup: (shop: string, body: { trackingNumbers: string[] }): Promise<ConfirmPickupResponse> =>
    post(`/operations/outbound/confirm-pickup${qs(shop)}`, body),

  bulkOutbound: (shop: string, body: { rows: { orderName: string }[]; notifyCustomers?: boolean }): Promise<OutboundBulkResponse> =>
    post(`/operations/outbound/bulk${qs(shop)}`, body),

  bulkOutboundPreview: (shop: string, rows: { orderName: string }[]): Promise<OutboundBulkPreviewResponse> =>
    post(`/operations/outbound/bulk/preview${qs(shop)}`, { rows }),

  getPickupStatus: (shop: string): Promise<PickupStatusResponse> =>
    get(`/operations/outbound/pickup-status${qs(shop)}`),

  overviewStats: (shop: string, period = 'today'): Promise<OverviewStatsResponse> =>
    get(`/operations/overview-stats${qs(shop)}&period=${encodeURIComponent(period)}`),

  orderStats: (shop: string): Promise<OrderStatsResponse> =>
    get(`/system-health/order-stats${qs(shop)}`),

  createWaybills: (
    shop: string,
    orders: {
      orderNumber:    string
      shopifyOrderId: string
      orderType:      'send' | 'exchange' | 'return'
      customerName:   string
      customerPhone:  string
      city:           string
      address:        string
      codAmount:      number
      items:          { sku: string; quantity: number }[]
    }[],
  ): Promise<{
    created: { orderName: string; trackingNumber: string }[]
    failed:  { orderName: string; error: string }[]
  }> =>
    post(`/orders/send-to/create-waybills${qs(shop)}`, { orders }),
}
